// utils/OrbitalElements.js

// Standard J2000 epoch mean orbital elements (angles in degrees, a in AU).
// Source: widely published planetary orbital element tables (NASA/JPL fact
// sheets, Standish 1992 reference values). These are mean elements at J2000
// — very close to reality, but not identical to Horizons' instantaneous
// osculating ellipse for "today," since real orbits perturb slightly over
// time. Good enough for an accurate-looking, stable orbit path.
export const ORBITAL_ELEMENTS = {
  Mercury: { a: 0.387098, e: 0.205630, i: 7.005,   omega: 48.331,  argPeriapsis: 29.124 },
  Venus:   { a: 0.723332, e: 0.006772, i: 3.39458, omega: 76.680,  argPeriapsis: 54.884 },
  Earth:   { a: 1.000000, e: 0.0167086,i: 0.00005, omega: 348.739, argPeriapsis: 114.208 },
  Moon:    { a: 0.0025696,e: 0.0549,   i: 5.145,   omega: 125.08,  argPeriapsis: 318.15 },
  Mars:    { a: 1.523679, e: 0.0934,   i: 1.850,   omega: 49.558,  argPeriapsis: 286.502 },
  Jupiter: { a: 5.2044,   e: 0.0489,   i: 1.303,   omega: 100.464, argPeriapsis: 273.867 },
  Saturn:  { a: 9.5826,   e: 0.0565,   i: 2.485,   omega: 113.665, argPeriapsis: 339.392 },
  Uranus:  { a: 19.2184,  e: 0.0457,   i: 0.773,   omega: 74.006,  argPeriapsis: 96.999 },
  Neptune: { a: 30.1104,  e: 0.008678, i: 1.770,   omega: 131.784, argPeriapsis: 273.187 },
  Pluto:   { a: 39.482,   e: 0.2488,   i: 17.140,  omega: 110.299, argPeriapsis: 113.834 },
};

const KUIPER_BELT = {
  innerAU: ORBITAL_ELEMENTS.Neptune.a,
  outerAU: 50,
};

function deg2rad(deg) {
  return (deg * Math.PI) / 180;
}

function rotateZ({ x, y, z }, angleRad) {
  const cos = Math.cos(angleRad), sin = Math.sin(angleRad);
  return { x: x * cos - y * sin, y: x * sin + y * cos, z };
}

function rotateX({ x, y, z }, angleRad) {
  const cos = Math.cos(angleRad), sin = Math.sin(angleRad);
  return { x, y: y * cos - z * sin, z: y * sin + z * cos };
}

/**
 * Computes points along a planet's true orbital ellipse, projected into the
 * same x/y plane used for current planet positions (z dropped), in AU units.
 * Applies argument of periapsis, inclination, and longitude of ascending
 * node in the standard order so the resulting ellipse has the Sun at one
 * focus and matches the planet's real orbital orientation.
 */
export function computeOrbitPathAU(planetName, numPoints = 180) {
  const elements = ORBITAL_ELEMENTS[planetName];
  if (!elements) return [];

  const { a, e, i, omega, argPeriapsis } = elements;
  const iRad = deg2rad(i);
  const omegaRad = deg2rad(omega);
  const argPeriRad = deg2rad(argPeriapsis);

  const points = [];
  for (let k = 0; k <= numPoints; k++) {
    const nu = (k / numPoints) * 2 * Math.PI; // true anomaly
    const r = (a * (1 - e * e)) / (1 + e * Math.cos(nu));

    let vec = { x: r * Math.cos(nu), y: r * Math.sin(nu), z: 0 };
    vec = rotateZ(vec, argPeriRad); // orient periapsis within orbital plane
    vec = rotateX(vec, iRad);       // tilt by inclination
    vec = rotateZ(vec, omegaRad);   // rotate ascending node into reference frame

    points.push({ x: vec.x, y: vec.y }); // drop z — same projection as current positions
  }
  return points;
}